import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, CheckCircle2, Clock, Download, Loader2, RefreshCcw, XCircle } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, downloadFile } from "@/lib/api";
import { trackConversionPixel } from "@/lib/oaiq";
import { Logo } from "@/components/Logo";

const inr = (n) => `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between gap-6 py-2 border-b border-zinc-800/70 last:border-0">
    <span className="text-xs text-zinc-500">{label}</span>
    <span data-testid={testid} className="text-sm font-medium text-zinc-200 font-num text-right break-all">{value}</span>
  </div>
);

export default function PaymentStatus() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [state, setState] = useState("checking");
  const tries = useRef(0);
  const timer = useRef(null);

  const check = useCallback(async () => {
    try {
      const { data } = await api.post(`/billing/paytm/${orderId}/verify`);
      const { data: summary } = await api.get(`/billing/paytm/order/${orderId}`);
      setOrder(summary);
      setState(data.status);
      if (data.status === "pending" && tries.current < 10) {
        tries.current += 1;
        timer.current = setTimeout(check, 4000);
      }
    } catch (e) {
      setState("failed");
      toast.error(apiError(e));
    }
  }, [orderId]);

  useEffect(() => {
    check();
    return () => clearTimeout(timer.current);
  }, [check]);

  // ChatGPT Ads subscription_created: pixel copy of the server's verified CAPI event.
  useEffect(() => {
    if (state !== "success" || !order || order.kind === "credits") return;
    trackConversionPixel(
      "subscription_created",
      { type: "plan_enrollment" },
      `sub_${orderId}`,
    );
  }, [state, order, orderId]);

  const retry = () =>
    navigate(order?.kind === "credits" ? "/checkout?credits=25" : `/checkout?plan=${order?.plan || "monthly"}`);

  const invoice = async () => {
    try {
      await downloadFile(`/billing/paytm/order/${orderId}/invoice.pdf`, `${order.invoice_no}.pdf`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const head =
    {
      checking: {
        icon: Loader2,
        tone: "text-indigo-300",
        title: "Confirming your payment…",
        note: "We are verifying the transaction with the bank. This takes a few seconds.",
      },
      pending: {
        icon: Clock,
        tone: "text-amber-300",
        title: "Payment is still processing",
        note: "The bank has not confirmed yet. Keep this page open — we are checking every few seconds.",
      },
      success: {
        icon: CheckCircle2,
        tone: "text-emerald-400",
        title: "Payment successful",
        note: "Your subscription is active and your credits are ready to use.",
      },
      failed: {
        icon: XCircle,
        tone: "text-red-400",
        title: "Payment could not be completed",
        note: "Nothing has been added to your plan. You can safely try again.",
      },
    }[state] || {};
  const Icon = head.icon || Loader2;

  return (
    <div className="min-h-screen nv-grid-bg text-zinc-100">
      <div className="mx-auto max-w-xl px-5 py-14">
        <Logo size={28} withWordmark />

        <div data-testid="payment-status-card" data-status={state} className="mt-10 nv-card p-6">
          <Icon className={`w-9 h-9 ${head.tone} ${state === "checking" ? "animate-spin" : ""}`} />
          <h1 data-testid="payment-status-title" className="mt-4 text-2xl font-extrabold tracking-tight">
            {head.title}
          </h1>
          <p className="mt-1.5 text-sm text-zinc-400">{head.note}</p>

          {order && (
            <div className="mt-6 rounded-xl border border-zinc-800 p-4">
              <Row label="Item" value={order.line?.title} testid="status-item" />
              <Row label="Amount paid" value={inr(order.tax?.total_inr || 0)} testid="status-amount" />
              <Row label="Invoice number" value={order.invoice_no} testid="status-invoice-no" />
              <Row label="Order ID" value={order.order_id} />
              {order.txn_id && <Row label="Transaction reference" value={order.txn_id} testid="status-txn-id" />}
              {order.method && <Row label="Method" value={order.method} />}
              {state === "success" && order.subscription && (
                <>
                  <Row
                    label="Subscription"
                    value={`${order.subscription.plan_label} · ${order.subscription.status}`}
                    testid="status-subscription"
                  />
                  {order.subscription.period_end && (
                    <Row
                      label="Valid until"
                      value={new Date(order.subscription.period_end).toLocaleDateString()}
                      testid="status-valid-until"
                    />
                  )}
                  <Row label="Credits available" value={order.subscription.proposals_left} />
                </>
              )}
            </div>
          )}

          <div className="mt-6 flex flex-wrap gap-2.5">
            {state === "success" && (
              <>
                <button
                  data-testid="continue-dashboard-btn"
                  onClick={() => navigate("/dashboard")}
                  className="h-11 px-5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium inline-flex items-center gap-2 transition-colors"
                >
                  Continue to dashboard <ArrowRight className="w-4 h-4" />
                </button>
                <button
                  data-testid="download-invoice-btn"
                  onClick={invoice}
                  className="h-11 px-4 rounded-xl border border-zinc-800 hover:border-zinc-700 text-sm text-zinc-300 inline-flex items-center gap-2 transition-colors"
                >
                  <Download className="w-4 h-4" /> Download invoice
                </button>
              </>
            )}

            {state === "failed" && (
              <>
                <button
                  data-testid="retry-payment-btn"
                  onClick={retry}
                  className="h-11 px-5 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium inline-flex items-center gap-2 transition-colors"
                >
                  <RefreshCcw className="w-4 h-4" /> Retry payment
                </button>
                <button
                  data-testid="back-to-plans-btn"
                  onClick={() => navigate("/plans")}
                  className="h-11 px-4 rounded-xl border border-zinc-800 hover:border-zinc-700 text-sm text-zinc-300 transition-colors"
                >
                  Back to checkout
                </button>
              </>
            )}

            {(state === "pending" || state === "checking") && (
              <button
                data-testid="recheck-payment-btn"
                onClick={check}
                className="h-11 px-4 rounded-xl border border-zinc-800 hover:border-zinc-700 text-sm text-zinc-300 inline-flex items-center gap-2 transition-colors"
              >
                <RefreshCcw className="w-4 h-4" /> Check again
              </button>
            )}
          </div>
        </div>

        <p className="mt-4 text-[11px] text-zinc-500">
          Plans and credits are activated only after the payment is verified on our server — never from the browser.
        </p>
      </div>
    </div>
  );
}
